import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Eye, Send, Users, AlertTriangle, Mail, Monitor, Smartphone, BarChart3,
} from "lucide-react";

/**
 * Editor de campaña (Fase 13.5). Vive sobre el template base — el usuario
 * puede override subject/preheader y ajustar segment + send_type.
 * Preview reusa el endpoint `/admin/email-campaigns/{id}/preview`.
 */
export default function EmailCampaignEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const superadmin = user?.roles?.includes("superadmin_dcc");
  const base = superadmin ? "/app/superadmin" : "/app/staff";

  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [audienceEstimate, setAudienceEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewMeta, setPreviewMeta] = useState(null);
  const [viewport, setViewport] = useState("desktop");
  const [sending, setSending] = useState(false);
  const savingTimer = useRef(null);
  const lastSaved = useRef("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/email-campaigns/${id}`);
      setCampaign(r.data);
      setAudienceEstimate(r.data.audience_count);
      lastSaved.current = JSON.stringify(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      nav(`${base}/email/campaigns`);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [id]);

  const patchCampaign = (partial) => {
    setCampaign((c) => ({ ...c, ...partial }));
  };
  const patchSegment = (partial) => {
    setCampaign((c) => ({ ...c, segment: { ...(c.segment || {}), ...partial } }));
  };
  const patchOverrides = (partial) => {
    setCampaign((c) => ({ ...c, overrides: { ...(c.overrides || {}), ...partial } }));
  };

  const save = async (silent = false) => {
    if (!campaign) return;
    setSaving(true);
    try {
      const payload = {
        name: campaign.name,
        template_id: campaign.template_id,
        status: campaign.status,
        send_type: campaign.send_type,
        trigger_event: campaign.trigger_event,
        trigger_delay_minutes: campaign.trigger_delay_minutes,
        scheduled_at: campaign.scheduled_at,
        segment: campaign.segment || {},
        overrides: campaign.overrides || {},
      };
      const r = await api.patch(`/admin/email-campaigns/${id}`, payload);
      lastSaved.current = JSON.stringify(r.data);
      if (!silent) toast.success("Guardado");
    } catch (e) {
      if (!silent) toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!campaign) return;
    if (savingTimer.current) clearTimeout(savingTimer.current);
    const j = JSON.stringify(campaign);
    if (j !== lastSaved.current) {
      savingTimer.current = setTimeout(() => save(true), 2500);
    }
    return () => savingTimer.current && clearTimeout(savingTimer.current);
    // eslint-disable-next-line
  }, [campaign]);

  const estimateAudience = async () => {
    await save(true);
    setEstimating(true);
    try {
      const r = await api.post(`/admin/email-campaigns/${id}/estimate-audience`, {});
      setAudienceEstimate(r.data.audience_count);
      toast.success(`Audiencia: ${r.data.audience_count} destinatarios`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setEstimating(false);
    }
  };

  const runPreview = async () => {
    await save(true);
    try {
      const r = await api.post(`/admin/email-campaigns/${id}/preview`, {});
      setPreviewHtml(r.data.html);
      setPreviewMeta({ subject: r.data.subject, preheader: r.data.preheader, to: r.data.recipient_email });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const sendMock = async () => {
    if (!confirm("¿Enviar MOCKEADO? Generará deliveries + eventos simulados y NO se enviará email real.")) return;
    setSending(true);
    try {
      const r = await api.post(`/admin/email-campaigns/${id}/send-mock`);
      toast.success(`Envío MOCKEADO OK · ${r.data.sent_count} deliveries`);
      await load();
      nav(`${base}/email/campaigns/${id}/metrics`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (loading || !campaign) {
    return (
      <StaffLayout user={user} superadmin={superadmin}>
        <p className="text-center text-gray-400 py-12">Cargando campaña…</p>
      </StaffLayout>
    );
  }

  const isEditable = campaign.status !== "sent" && campaign.status !== "sending";

  return (
    <StaffLayout user={user} superadmin={superadmin}>
      <div className="mb-4">
        <Link to={`${base}/email/campaigns`} className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-white">
          <ArrowLeft className="w-3 h-3" /> Volver a campañas
        </Link>
      </div>

      <div className="mb-4 p-3 rounded-md bg-amber-500/10 border border-amber-500/30 flex items-start gap-2" data-testid="mock-banner-email">
        <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-200">
          <strong>Envío MOCKEADO</strong> — la campaña no se envía a un proveedor real (Resend/SendGrid). Se generan deliveries + eventos simulados con distribución realista de aperturas y clicks.
        </p>
      </div>

      {/* Top bar */}
      <div className="mb-4 p-4 rounded-xl border border-gray-800 bg-[#111827] flex flex-wrap items-center gap-3">
        <input
          value={campaign.name}
          onChange={(e) => patchCampaign({ name: e.target.value })}
          disabled={!isEditable}
          data-testid="campaign-name-input"
          className="flex-1 min-w-[200px] bg-transparent border border-gray-800 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-primary disabled:opacity-60"
        />
        <span
          data-testid="campaign-status-badge"
          className="text-[10px] px-2 py-0.5 rounded uppercase font-bold border border-gray-800 text-gray-400"
        >
          {campaign.status}
        </span>
        <button
          onClick={() => save(false)}
          disabled={saving || !isEditable}
          data-testid="campaign-save-btn"
          className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-primary hover:brightness-110 text-primary-foreground text-sm font-semibold disabled:opacity-60"
        >
          <Save className="w-4 h-4" /> {saving ? "…" : "Guardar"}
        </button>
        <button
          onClick={runPreview}
          data-testid="email-editor-preview-btn"
          className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-gray-800 text-white text-sm font-semibold hover:bg-white/5"
        >
          <Eye className="w-4 h-4" /> Preview
        </button>
        {isEditable && (
          <button
            onClick={sendMock}
            disabled={sending || !audienceEstimate}
            data-testid="email-editor-send-btn"
            className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-emerald-600 hover:brightness-110 text-white text-sm font-semibold disabled:opacity-60"
          >
            <Send className="w-4 h-4" /> {sending ? "Enviando…" : "Enviar MOCK"}
          </button>
        )}
        {campaign.status === "sent" && (
          <Link
            to={`${base}/email/campaigns/${id}/metrics`}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-emerald-600 hover:brightness-110 text-white text-sm font-semibold"
          >
            <BarChart3 className="w-4 h-4" /> Ver métricas
          </Link>
        )}
      </div>

      {/* 3 paneles */}
      <div className="grid lg:grid-cols-[280px_1fr_320px] gap-4">
        {/* IZQUIERDA · asunto + preheader + template info */}
        <div className="space-y-4">
          <div className="rounded-xl border border-gray-800 bg-[#111827] p-4">
            <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2">Asunto y preheader</p>
            <input
              value={(campaign.overrides?.subject ?? "") || (campaign.template?.subject_template || "")}
              onChange={(e) => patchOverrides({ subject: e.target.value })}
              disabled={!isEditable}
              placeholder="Asunto"
              data-testid="campaign-subject-input"
              className="w-full mb-2 bg-[#0B0F14] border border-gray-800 rounded-md px-2 py-1.5 text-xs text-white focus:outline-none focus:border-primary"
            />
            <input
              value={(campaign.overrides?.preheader ?? "") || (campaign.template?.preheader || "")}
              onChange={(e) => patchOverrides({ preheader: e.target.value })}
              disabled={!isEditable}
              placeholder="Preheader"
              data-testid="campaign-preheader-input"
              className="w-full bg-[#0B0F14] border border-gray-800 rounded-md px-2 py-1.5 text-xs text-white focus:outline-none focus:border-primary"
            />
            <p className="mt-3 text-[10px] uppercase tracking-widest text-gray-500 font-bold">Plantilla base</p>
            <Link
              to={`${base}/email/templates/${campaign.template_id}`}
              className="mt-1 block text-sm text-primary hover:underline"
            >
              {campaign.template?.name || campaign.template_slug}
            </Link>
            <p className="text-xs text-gray-500">{campaign.template?.blocks?.length || 0} bloques · {campaign.template?.category}</p>
          </div>

          {campaign.send_type === "drip_trigger" && (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
              <p className="text-[10px] uppercase tracking-widest text-amber-400 font-bold mb-2">Drip trigger</p>
              <p className="text-xs text-gray-300">Se dispara al evento:</p>
              <p className="text-sm font-bold text-white mt-1">{campaign.trigger_event}</p>
              <p className="text-xs text-gray-400 mt-1">Delay: {campaign.trigger_delay_minutes} min</p>
            </div>
          )}
        </div>

        {/* CENTRO · preview */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <div className="flex gap-1">
              <button
                onClick={() => setViewport("desktop")}
                className={`p-1.5 rounded ${viewport === "desktop" ? "bg-primary/20 text-primary" : "text-gray-400"}`}
                aria-label="Desktop"
              >
                <Monitor className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewport("mobile")}
                className={`p-1.5 rounded ${viewport === "mobile" ? "bg-primary/20 text-primary" : "text-gray-400"}`}
                aria-label="Mobile"
              >
                <Smartphone className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Preview</p>
          </div>
          {/* Inbox mock */}
          <div className="rounded-t-lg border border-b-0 border-gray-800 bg-[#111827] p-3">
            <div className="flex items-start gap-2">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex-shrink-0 flex items-center justify-center">
                <Mail className="w-4 h-4 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-gray-400">Debt Capital College &lt;no-reply@dcc.mx&gt; → {previewMeta?.to || "—"}</p>
                <p className="text-sm font-bold text-white truncate">
                  {previewMeta?.subject || campaign.overrides?.subject || campaign.template?.subject_template || "Sin asunto"}
                </p>
                <p className="text-xs text-gray-400 truncate">
                  {previewMeta?.preheader || campaign.overrides?.preheader || campaign.template?.preheader || ""}
                </p>
              </div>
            </div>
          </div>
          <div
            className="border border-gray-800 rounded-b-lg bg-[#0B0F14] overflow-hidden"
            style={{ minHeight: 500 }}
          >
            {previewHtml ? (
              <iframe
                title="campaign-preview"
                srcDoc={previewHtml}
                data-testid="campaign-preview-iframe"
                className="bg-white block"
                style={{
                  width: viewport === "mobile" ? 375 : "100%",
                  maxWidth: "100%",
                  height: 700,
                  margin: viewport === "mobile" ? "0 auto" : "0",
                  border: "none",
                }}
              />
            ) : (
              <div className="p-6 flex flex-col items-center justify-center text-gray-500 text-sm space-y-3" style={{ minHeight: 500 }}>
                <Eye className="w-10 h-10 opacity-40" />
                <p>Haz clic en "Preview" para renderizar</p>
              </div>
            )}
          </div>
        </div>

        {/* DERECHA · segment builder */}
        <div className="rounded-xl border border-gray-800 bg-[#111827] p-4 space-y-4">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-primary font-bold flex items-center gap-1 mb-2">
              <Users className="w-3 h-3" /> Segmentación
            </p>
            <p className="text-xs text-gray-400 mb-3">
              Define quién recibe la campaña. Los transaccionales ignoran las bajas.
            </p>
          </div>
          <SegField
            label="Roles (coma)"
            value={(campaign.segment?.roles || []).join(", ")}
            onChange={(v) => patchSegment({ roles: v.split(",").map((x) => x.trim()).filter(Boolean) })}
            disabled={!isEditable}
            placeholder="alumno"
            testid="seg-roles"
          />
          <SegField
            label="Tenant slugs (coma)"
            value={(campaign.segment?.tenant_slugs || []).join(", ")}
            onChange={(v) => patchSegment({ tenant_slugs: v.split(",").map((x) => x.trim()).filter(Boolean) })}
            disabled={!isEditable}
            placeholder="dcc, banco-alfa"
            testid="seg-tenants"
          />
          <SegField
            label="Con programas (slugs, coma)"
            value={(campaign.segment?.has_program_slugs || []).join(", ")}
            onChange={(v) => patchSegment({ has_program_slugs: v.split(",").map((x) => x.trim()).filter(Boolean) })}
            disabled={!isEditable}
            placeholder="high-performance-client-management-hpcm"
            testid="seg-programs"
          />
          <SegField
            label="Con planes (slugs, coma)"
            value={(campaign.segment?.subscribed_to_plan_slugs || []).join(", ")}
            onChange={(v) => patchSegment({ subscribed_to_plan_slugs: v.split(",").map((x) => x.trim()).filter(Boolean) })}
            disabled={!isEditable}
            placeholder="all-access-annual"
            testid="seg-plans"
          />
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!campaign.segment?.exclude_unsubscribed}
              onChange={(e) => patchSegment({ exclude_unsubscribed: e.target.checked })}
              disabled={!isEditable}
              className="w-4 h-4 accent-primary"
              data-testid="seg-exclude-unsub"
            />
            <span className="text-xs text-white">Excluir bajas</span>
          </label>

          <button
            onClick={estimateAudience}
            disabled={estimating}
            data-testid="campaign-estimate-audience-btn"
            className="w-full py-2 rounded-md bg-primary/10 hover:bg-primary/20 text-primary text-xs font-bold uppercase tracking-widest border border-primary/30 disabled:opacity-60"
          >
            {estimating ? "Estimando…" : "Estimar audiencia"}
          </button>

          {audienceEstimate !== null && (
            <div className="p-3 rounded-md bg-emerald-500/5 border border-emerald-500/30 text-center">
              <p className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold">Audiencia</p>
              <p className="text-3xl font-extrabold text-white tabular-nums" data-testid="audience-count">
                {Number(audienceEstimate).toLocaleString("es-MX")}
              </p>
              <p className="text-[10px] text-gray-500">destinatarios</p>
            </div>
          )}
        </div>
      </div>
    </StaffLayout>
  );
}

function SegField({ label, value, onChange, disabled, placeholder, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-2 py-1.5 text-xs text-white focus:outline-none focus:border-primary disabled:opacity-60"
      />
    </label>
  );
}
